import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Building2,
  Users,
  Navigation,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useDocumentSEO } from "@/hooks/useDocumentSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Mimari Proje",
  "İnşaat Taahhüt",
  "Anahtar Teslim",
  "Gayrimenkul Geliştirme",
  "Diğer",
];

const PHONE_DISPLAY = "(0216) 499 10 01";
const PHONE_TEL = "+902164991001";
const EMAIL = "info@1001insaat.com.tr";

const ADRESLER = [
  {
    key: "merkez",
    label: "Merkez",
    line1: "Eyüp Sultan, İmam Rabbani Cd. No:34/B",
    line2: "34885 Sancaktepe / İstanbul",
    query: "Eyüp Sultan Mah. İmam Rabbani Cd. No:34/B 34885 Sancaktepe İstanbul",
  },
  {
    key: "yazisma",
    label: "İletişim / Yazışma Adresi",
    line1: "Fatih, İnci Cd. No:75",
    line2: "34920 Sultanbeyli / İstanbul",
    query: "Fatih Mah. İnci Cd. No:75 34920 Sultanbeyli İstanbul",
  },
];

const embedUrl = (query) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(
    query
  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

const directionsUrl = (query) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;

const initialTeklif = {
  name: "",
  company: "",
  phone: "",
  email: "",
  project_type: "",
  message: "",
  kvkk_consent: false,
};

const initialIsBirligi = {
  name: "",
  company: "",
  phone: "",
  email: "",
  message: "",
  kvkk_consent: false,
};

const inputBase =
  "w-full bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none py-3 text-[14px] placeholder:text-anthracite/40 text-navy transition-colors";

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className="overline text-anthracite/60 block mb-2">
        {label}
        {required && " *"}
      </label>
      {children}
      {error && <p className="mt-2 text-[12px] text-red-600">{error}</p>}
    </div>
  );
}

function SuccessCard({ onReset, formLabel }) {
  return (
    <div
      data-testid="iletisim-success"
      className="border border-gold/40 bg-white p-8 lg:p-12 text-center relative"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gold" />
      <div className="mx-auto w-14 h-14 border border-gold flex items-center justify-center text-gold mb-6">
        <CheckCircle2 size={24} strokeWidth={1.5} />
      </div>
      <div className="overline text-anthracite/60 mb-3">
        {formLabel} · İletildi
      </div>
      <h3 className="font-heading text-2xl md:text-3xl font-light text-navy tracking-tight">
        Mesajınız bize ulaştı.
      </h3>
      <p className="mt-4 max-w-md mx-auto text-[14.5px] text-anthracite/80 leading-relaxed font-light">
        En kısa sürede geri dönüş yapacağız. İletişim tercihinize göre e-posta
        veya telefonla sizinle iletişime geçeceğiz.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        <button
          type="button"
          onClick={onReset}
          data-testid="iletisim-success-new"
          className="inline-flex items-center gap-2 border border-navy/20 hover:border-gold hover:text-gold text-navy px-6 py-3 text-[12px] tracking-widest uppercase font-medium transition-colors"
        >
          Yeni Bir Mesaj
        </button>
        <a
          href={`mailto:${EMAIL}`}
          className="inline-flex items-center gap-2 bg-navy hover:bg-gold text-white px-6 py-3 text-[12px] tracking-widest uppercase font-medium transition-colors"
        >
          E-Posta ile Yaz
          <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

/* ---------- TEKLİF FORM ---------- */
function TeklifForm({ onSuccess }) {
  const [form, setForm] = useState(initialTeklif);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Ad Soyad zorunludur.";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Geçerli bir e-posta giriniz.";
    if (!form.project_type) e.project_type = "Proje türü seçiniz.";
    if (!form.message || form.message.trim().length < 5) e.message = "Proje detayını yazınız.";
    if (!form.kvkk_consent) e.kvkk_consent = "Devam etmek için onaylamalısınız.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        project_type: form.project_type,
        message: form.message,
        kvkk_consent: form.kvkk_consent,
        subject: `Teklif Talebi · ${form.project_type}`,
        form_type: "teklif",
      });
      toast.success("Teklif talebiniz iletildi.");
      onSuccess();
      setForm(initialTeklif);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Talep gönderilemedi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="teklif-form"
      noValidate
      className="border border-navy/10 p-8 lg:p-10 bg-white"
    >
      <div className="overline text-anthracite/70">Teklif Formu</div>
      <h3 className="mt-2 font-heading text-2xl md:text-3xl font-light text-navy tracking-tight">
        Projeniz için teklif alın
      </h3>
      <div className="mt-3 w-10 h-[1px] bg-gold" />

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <FormField label="Ad Soyad" required error={errors.name}>
          <input
            type="text"
            autoComplete="name"
            className={inputBase}
            placeholder="Adınız Soyadınız"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="teklif-name"
          />
        </FormField>

        <FormField label="Şirket">
          <input
            type="text"
            autoComplete="organization"
            className={inputBase}
            placeholder="Şirket adı (opsiyonel)"
            value={form.company}
            onChange={(e) => set("company", e.target.value)}
            data-testid="teklif-company"
          />
        </FormField>

        <FormField label="Telefon">
          <input
            type="tel"
            autoComplete="tel"
            className={inputBase}
            placeholder="+90 ..."
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            data-testid="teklif-phone"
          />
        </FormField>

        <FormField label="E-Posta" required error={errors.email}>
          <input
            type="email"
            autoComplete="email"
            className={inputBase}
            placeholder="ornek@sirket.com"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            data-testid="teklif-email"
          />
        </FormField>

        <FormField label="Proje Türü" required error={errors.project_type}>
          <div className="relative">
            <select
              className={`${inputBase} appearance-none pr-8 cursor-pointer`}
              value={form.project_type}
              onChange={(e) => set("project_type", e.target.value)}
              data-testid="teklif-project-type"
            >
              <option value="">Seçiniz</option>
              {PROJECT_TYPES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-gold pointer-events-none"
            />
          </div>
        </FormField>

        <div className="md:col-span-2">
          <FormField label="Proje Detayı" required error={errors.message}>
            <textarea
              rows={5}
              className={`${inputBase} resize-none`}
              placeholder="Proje kapsamı, konum, hedef ölçek, öncelikleriniz..."
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              data-testid="teklif-message"
            />
          </FormField>
        </div>
      </div>

      <label className="mt-8 flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={form.kvkk_consent}
          onChange={(e) => set("kvkk_consent", e.target.checked)}
          data-testid="teklif-kvkk"
          className="mt-1 w-4 h-4 accent-gold shrink-0"
        />
        <span className="text-[12.5px] leading-relaxed text-anthracite/80">
          Kişisel verilerimin{" "}
          <Link
            to="/kvkk-aydinlatma-metni"
            target="_blank"
            className="text-navy underline underline-offset-2 hover:text-gold"
          >
            KVKK Aydınlatma Metni
          </Link>{" "}
          kapsamında işlenmesini onaylıyorum. *
        </span>
      </label>
      {errors.kvkk_consent && (
        <p className="mt-2 text-[12px] text-red-600" data-testid="teklif-error-kvkk">
          {errors.kvkk_consent}
        </p>
      )}

      <div className="mt-10">
        <button
          type="submit"
          disabled={loading}
          data-testid="teklif-submit"
          className="group inline-flex items-center gap-3 bg-navy hover:bg-gold text-white px-8 py-3.5 text-[12.5px] tracking-widest uppercase font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Gönderiliyor..." : "Teklif Talebini Gönder"}
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
}

/* ---------- İŞ BİRLİĞİ FORM ---------- */
function IsBirligiForm({ onSuccess }) {
  const [form, setForm] = useState(initialIsBirligi);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Ad Soyad zorunludur.";
    if (!form.company || form.company.trim().length < 2) e.company = "Kurum/Şirket zorunludur.";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Geçerli bir e-posta giriniz.";
    if (!form.message || form.message.trim().length < 5) e.message = "İş birliği konusunu yazınız.";
    if (!form.kvkk_consent) e.kvkk_consent = "Devam etmek için onaylamalısınız.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: form.message,
        kvkk_consent: form.kvkk_consent,
        subject: "İş Birliği Talebi",
        form_type: "is_birligi",
      });
      toast.success("İş birliği talebiniz iletildi.");
      onSuccess();
      setForm(initialIsBirligi);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Talep gönderilemedi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="isbirligi-form"
      noValidate
      className="border border-navy/10 p-8 lg:p-10 bg-white"
    >
      <div className="overline text-anthracite/70">İş Birliği Formu</div>
      <h3 className="mt-2 font-heading text-2xl md:text-3xl font-light text-navy tracking-tight">
        Kurumsal iş birliği talebi
      </h3>
      <div className="mt-3 w-10 h-[1px] bg-gold" />

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <FormField label="Ad Soyad" required error={errors.name}>
          <input
            type="text"
            autoComplete="name"
            className={inputBase}
            placeholder="Adınız Soyadınız"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="isbirligi-name"
          />
        </FormField>

        <FormField label="Kurum / Şirket" required error={errors.company}>
          <input
            type="text"
            autoComplete="organization"
            className={inputBase}
            placeholder="Kurum / şirket adı"
            value={form.company}
            onChange={(e) => set("company", e.target.value)}
            data-testid="isbirligi-company"
          />
        </FormField>

        <FormField label="Telefon">
          <input
            type="tel"
            autoComplete="tel"
            className={inputBase}
            placeholder="+90 ..."
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            data-testid="isbirligi-phone"
          />
        </FormField>

        <FormField label="E-Posta" required error={errors.email}>
          <input
            type="email"
            autoComplete="email"
            className={inputBase}
            placeholder="kurumsal@sirket.com"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            data-testid="isbirligi-email"
          />
        </FormField>

        <div className="md:col-span-2">
          <FormField label="İş Birliği Konusu" required error={errors.message}>
            <textarea
              rows={5}
              className={`${inputBase} resize-none`}
              placeholder="İş birliği kapsamı, ortaklaşa değerlendirilebilecek proje veya alan..."
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              data-testid="isbirligi-message"
            />
          </FormField>
        </div>
      </div>

      <label className="mt-8 flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={form.kvkk_consent}
          onChange={(e) => set("kvkk_consent", e.target.checked)}
          data-testid="isbirligi-kvkk"
          className="mt-1 w-4 h-4 accent-gold shrink-0"
        />
        <span className="text-[12.5px] leading-relaxed text-anthracite/80">
          Kişisel verilerimin{" "}
          <Link
            to="/kvkk-aydinlatma-metni"
            target="_blank"
            className="text-navy underline underline-offset-2 hover:text-gold"
          >
            KVKK Aydınlatma Metni
          </Link>{" "}
          kapsamında işlenmesini onaylıyorum. *
        </span>
      </label>
      {errors.kvkk_consent && (
        <p className="mt-2 text-[12px] text-red-600" data-testid="isbirligi-error-kvkk">
          {errors.kvkk_consent}
        </p>
      )}

      <div className="mt-10">
        <button
          type="submit"
          disabled={loading}
          data-testid="isbirligi-submit"
          className="group inline-flex items-center gap-3 bg-navy hover:bg-gold text-white px-8 py-3.5 text-[12.5px] tracking-widest uppercase font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Gönderiliyor..." : "İş Birliği Talebini Gönder"}
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
}

/* ---------- ADDRESS + MAP CARD ---------- */
function AdresKarti({ adres, index }) {
  const isDark = index === 0;
  return (
    <div
      data-testid={`adres-karti-${adres.key}`}
      className={`flex flex-col border ${
        isDark
          ? "border-navy/15 bg-navy text-white"
          : "border-navy/15 bg-white text-navy"
      }`}
    >
      <div className="p-8 lg:p-10">
        <div className="flex items-center gap-3 mb-5">
          <span
            className={`w-10 h-10 border flex items-center justify-center shrink-0 text-gold ${
              isDark ? "border-gold/50" : "border-navy/20"
            }`}
          >
            <MapPin size={16} strokeWidth={1.5} />
          </span>
          <span
            className={`overline ${
              isDark ? "text-gold" : "text-anthracite/60"
            }`}
          >
            Ofis
          </span>
        </div>

        <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug">
          {adres.label}
        </h3>

        <div className="mt-6 w-12 h-[1px] bg-gold" />

        <p
          className={`mt-6 text-[15px] leading-relaxed font-light ${
            isDark ? "text-white/85" : "text-anthracite/85"
          }`}
        >
          {adres.line1}
          <br />
          {adres.line2}
        </p>

        <div className="mt-8">
          <a
            href={directionsUrl(adres.query)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`yol-tarifi-${adres.key}`}
            className={`group inline-flex items-center gap-3 px-5 py-3 text-[12px] tracking-widest uppercase font-medium transition-colors ${
              isDark
                ? "border border-gold bg-gold text-white hover:bg-gold-hover"
                : "border border-navy bg-navy text-white hover:bg-gold hover:border-gold"
            }`}
          >
            <Navigation size={14} />
            Yol Tarifi Al
            <ArrowUpRight
              size={14}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
        </div>
      </div>

      {/* Embedded map */}
      <div className="relative w-full aspect-[16/10] border-t border-navy/10 bg-navy">
        <iframe
          title={`Harita — ${adres.label}`}
          src={embedUrl(adres.query)}
          data-testid={`harita-${adres.key}`}
          width="100%"
          height="100%"
          style={{ border: 0, display: "block" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen=""
        />
      </div>
    </div>
  );
}

export default function IletisimPage() {
  useDocumentSEO({
    title: "İletişim",
    description:
      "1001 Mimarlık İnşaat ve Ticaret A.Ş. ile iletişim. Teklif ve iş birliği talepleri için Sancaktepe merkez ve Sultanbeyli yazışma ofisimize ulaşın.",
  });
  const [activeTab, setActiveTab] = useState("teklif");
  const [submitted, setSubmitted] = useState(null);
  const handleReset = () => setSubmitted(null);

  return (
    <>
      <PageHeader
        breadcrumb="İletişim"
        overline="Bizimle Konuşun"
        title="İletişim"
        subtitle="Projeniz için teklif almak ya da iş birliği fırsatlarını konuşmak için bizimle iletişime geçin."
        backgroundImage="https://images.pexels.com/photos/2246476/pexels-photo-2246476.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800"
      />

      {/* 1. FORMS + CONTACT INFO */}
      <section
        data-testid="iletisim-main"
        className="bg-white py-20 lg:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left: Tabs + Form */}
          <div className="lg:col-span-8">
            <div
              data-testid="iletisim-tabs"
              className="grid grid-cols-2 border border-navy/10 mb-8"
            >
              <button
                type="button"
                onClick={() => {
                  setActiveTab("teklif");
                  setSubmitted(null);
                }}
                data-testid="tab-teklif"
                className={`relative px-6 py-5 text-left transition-colors ${
                  activeTab === "teklif"
                    ? "bg-navy text-white"
                    : "bg-white text-navy hover:bg-navy/5"
                }`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className={`w-10 h-10 border flex items-center justify-center shrink-0 text-gold ${
                      activeTab === "teklif" ? "border-gold/60" : "border-navy/15"
                    }`}
                  >
                    <Building2 size={16} strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="font-heading text-[15px] font-medium tracking-tight">
                      Teklif Talebi
                    </div>
                    <div
                      className={`mt-1 text-[11.5px] leading-relaxed ${
                        activeTab === "teklif"
                          ? "text-white/60"
                          : "text-anthracite/60"
                      }`}
                    >
                      Projenize özel teklif için
                    </div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveTab("isbirligi");
                  setSubmitted(null);
                }}
                data-testid="tab-isbirligi"
                className={`relative px-6 py-5 text-left transition-colors border-l border-navy/10 ${
                  activeTab === "isbirligi"
                    ? "bg-navy text-white"
                    : "bg-white text-navy hover:bg-navy/5"
                }`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className={`w-10 h-10 border flex items-center justify-center shrink-0 text-gold ${
                      activeTab === "isbirligi" ? "border-gold/60" : "border-navy/15"
                    }`}
                  >
                    <Users size={16} strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="font-heading text-[15px] font-medium tracking-tight">
                      İş Birliği Talebi
                    </div>
                    <div
                      className={`mt-1 text-[11.5px] leading-relaxed ${
                        activeTab === "isbirligi"
                          ? "text-white/60"
                          : "text-anthracite/60"
                      }`}
                    >
                      Kurumsal iş birliği için
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {submitted ? (
              <SuccessCard
                onReset={handleReset}
                formLabel={submitted === "teklif" ? "Teklif Talebi" : "İş Birliği"}
              />
            ) : activeTab === "teklif" ? (
              <TeklifForm onSuccess={() => setSubmitted("teklif")} />
            ) : (
              <IsBirligiForm onSuccess={() => setSubmitted("isbirligi")} />
            )}
          </div>

          {/* Right: Common contact info */}
          <aside className="lg:col-span-4" data-testid="iletisim-info">
            <div className="border border-navy/10 bg-white p-8">
              <div className="overline text-anthracite/70">İletişim</div>
              <h3 className="mt-2 font-heading text-2xl font-light text-navy tracking-tight">
                Doğrudan kanallar
              </h3>
              <div className="mt-3 w-10 h-[1px] bg-gold" />

              <ul className="mt-8 space-y-7">
                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-navy/15 flex items-center justify-center text-gold shrink-0">
                    <Phone size={15} />
                  </span>
                  <div>
                    <div className="overline text-anthracite/60 mb-1">Telefon</div>
                    <a
                      href={`tel:${PHONE_TEL}`}
                      data-testid="iletisim-phone"
                      className="text-[14px] text-navy hover:text-gold transition-colors"
                    >
                      {PHONE_DISPLAY}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-navy/15 flex items-center justify-center text-gold shrink-0">
                    <Mail size={15} />
                  </span>
                  <div>
                    <div className="overline text-anthracite/60 mb-1">E-Posta</div>
                    <a
                      href={`mailto:${EMAIL}`}
                      data-testid="iletisim-email"
                      className="text-[14px] text-navy hover:text-gold transition-colors break-all"
                    >
                      {EMAIL}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-navy/15 flex items-center justify-center text-gold shrink-0">
                    <Clock size={15} />
                  </span>
                  <div>
                    <div className="overline text-anthracite/60 mb-1">Çalışma Saatleri</div>
                    <div className="text-[14px] text-navy leading-relaxed">
                      Hafta içi 09:00 – 18:00
                      <br />
                      <span className="text-anthracite/60 text-[13px]">
                        Cumartesi – Pazar kapalı
                      </span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-6 border border-navy/10 bg-white p-6">
              <div className="overline text-anthracite/60 mb-2">Grup</div>
              <div className="text-[13.5px] text-navy leading-relaxed">
                1001 Mimarlık İnşaat ve Ticaret A.Ş., 1001 Grup iştirakidir.
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 2. İKİ ADRES KARTI */}
      <section
        data-testid="iletisim-adresler"
        className="bg-anthracite text-white py-20 lg:py-28 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-14">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[1px] bg-gold" />
              <span className="overline text-gold">Adreslerimiz</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {ADRESLER.map((a, i) => (
              <AdresKarti key={a.key} adres={a} index={i} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
